import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, ChevronLeft, ZoomIn, ZoomOut, Printer } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/ArtistCardGenerator.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker using local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Avery Template Presets with Layout Specifications
const AVERY_TEMPLATES = {
  '5160': {
    name: 'Avery 5160 (1" × 2.625")',
    cardWidth: 2.625,
    cardHeight: 1,
    cardsPerRow: 3,
    cardsPerCol: 10,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      nameFontSize: 11,
      nameFontStyle: 'bold',
      nameY: 0.15,
      roleFontSize: 8,
      roleY: 0.3,
      contactFontSize: 7,
      contactY: 0.45,
      qrCodeSize: 0.4,
      qrCodeX: 2.1,
      qrCodeY: 0.2,
      contentStartX: 0.08,
      contentEndX: 2.05
    }
  },
  '5161': {
    name: 'Avery 5161 (1" × 4")',
    cardWidth: 4,
    cardHeight: 1,
    cardsPerRow: 2,
    cardsPerCol: 10,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      nameFontSize: 12,
      nameFontStyle: 'bold',
      nameY: 0.15,
      roleFontSize: 9,
      roleY: 0.3,
      contactFontSize: 8,
      contactY: 0.45,
      qrCodeSize: 0.45,
      qrCodeX: 3.4,
      qrCodeY: 0.15,
      contentStartX: 0.1,
      contentEndX: 3.3
    }
  },
  '5162': {
    name: 'Avery 5162 (1.5" × 4")',
    cardWidth: 4,
    cardHeight: 1.5,
    cardsPerRow: 2,
    cardsPerCol: 6,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      nameFontSize: 13,
      nameFontStyle: 'bold',
      nameY: 0.18,
      roleFontSize: 10,
      roleY: 0.4,
      contactFontSize: 9,
      contactY: 0.6,
      qrCodeSize: 0.5,
      qrCodeX: 3.3,
      qrCodeY: 0.2,
      contentStartX: 0.1,
      contentEndX: 3.2
    }
  },
  '8160': {
    name: 'Avery 8160 (1" × 2.625")',
    cardWidth: 2.625,
    cardHeight: 1,
    cardsPerRow: 3,
    cardsPerCol: 10,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      nameFontSize: 11,
      nameFontStyle: 'bold',
      nameY: 0.15,
      roleFontSize: 8,
      roleY: 0.3,
      contactFontSize: 7,
      contactY: 0.45,
      qrCodeSize: 0.4,
      qrCodeX: 2.1,
      qrCodeY: 0.2,
      contentStartX: 0.08,
      contentEndX: 2.05
    }
  },
  '8163': {
    name: 'Avery 8163 (2" × 4")',
    cardWidth: 4,
    cardHeight: 2,
    cardsPerRow: 2,
    cardsPerCol: 5,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      nameFontSize: 14,
      nameFontStyle: 'bold',
      nameY: 0.2,
      roleFontSize: 11,
      roleY: 0.4,
      contactFontSize: 9,
      contactY: 0.65,
      qrCodeSize: 0.6,
      qrCodeX: 3.2,
      qrCodeY: 0.25,
      contentStartX: 0.1,
      contentEndX: 3.1
    }
  },
  'custom_business': {
    name: 'Custom Business Card (3.5" × 2")',
    cardWidth: 3.5,
    cardHeight: 2,
    cardsPerRow: 2,
    cardsPerCol: 5,
    margin: 0.25,
    gap: 0.125,
    precut: false,
    layout: {
      nameFontSize: 13,
      nameFontStyle: 'bold',
      nameY: 0.2,
      roleFontSize: 10,
      roleY: 0.4,
      contactFontSize: 9,
      contactY: 0.6,
      qrCodeSize: 0.55,
      qrCodeX: 2.8,
      qrCodeY: 0.2,
      contentStartX: 0.12,
      contentEndX: 2.7
    }
  },
  'custom_square': {
    name: 'Custom Square (2" × 2")',
    cardWidth: 2,
    cardHeight: 2,
    cardsPerRow: 4,
    cardsPerCol: 5,
    margin: 0.25,
    gap: 0.125,
    precut: false,
    layout: {
      nameFontSize: 12,
      nameFontStyle: 'bold',
      nameY: 0.2,
      roleFontSize: 9,
      roleY: 0.4,
      contactFontSize: 8,
      contactY: 0.6,
      qrCodeSize: 0.5,
      qrCodeX: 1.25,
      qrCodeY: 0.2,
      contentStartX: 0.1,
      contentEndX: 1.2
    }
  }
};

const ArtistCardGenerator = () => {
  const { collectionId: collectionIdParam } = useParams();
  const navigate = useNavigate();
  
  // Convert collectionId to integer and validate
  const collectionId = collectionIdParam ? parseInt(collectionIdParam, 10) : null;
  
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [collection, setCollection] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState('');
  
  // Font size state variables
  const [artistNameFontSize, setArtistNameFontSize] = useState(14);
  const [roleFontSize, setRoleFontSize] = useState(10);
  const [contactFontSize, setContactFontSize] = useState(8);

  // New design customization options
  const [cardLayout, setCardLayout] = useState('standard'); // 'standard', 'minimal', 'creative'
  const [selectedTemplate, setSelectedTemplate] = useState('custom_business');
  const [showCutLines, setShowCutLines] = useState(false);
  const [qrCodeColor, setQrCodeColor] = useState('#000000');
  const [qrCodeBgColor, setQrCodeBgColor] = useState('#ffffff');
  const [includeVibrantCastLogo, setIncludeVibrantCastLogo] = useState(true);

  // PDF preview state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  // Fetch collection data and artist data
// Update the useEffect code to correctly handle the RPC response:
useEffect(() => {
    const fetchData = async () => {
      if (!collectionId || isNaN(collectionId)) {
        console.warn('Invalid collectionId provided to ArtistCardGenerator:', collectionId);
        setError('Invalid collection ID');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Load logo image and convert to data URL
        const loadLogo = async () => {
          try {
            console.log('Loading logo image...');
            const logoUrl = 'https://res.cloudinary.com/dh9jfogh1/image/upload/v1742436007/vibrant-logo_rtqeet.png';
            console.log('Logo URL:', logoUrl);
            
            const response = await fetch(logoUrl);
            console.log('Logo fetch response status:', response.status);
            
            if (!response.ok) {
              throw new Error(`Logo fetch failed: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            console.log('Logo blob size:', blob.size, 'bytes');
            
            const reader = new FileReader();
            reader.onloadend = () => {
              console.log('Logo converted to data URL, length:', reader.result.length);
              setLogoDataUrl(reader.result);
              setLogoLoaded(true);
            };
            reader.readAsDataURL(blob);
          } catch (error) {
            console.error('Error loading logo:', error);
            setLogoLoaded(true); // Continue without logo if it fails to load
          }
        };
        
        loadLogo();
        
        // Import the API utility functions
        const { fetchCollectionDetails } = await import('../utils/api');
        
        // Fetch collection info
        const collectionData = await fetchCollectionDetails(collectionId);
        setCollection(collectionData);
        
        // Fetch artists for this collection using Supabase RPC call
        const { data: rpcResult, error: rpcError } = await supabase.rpc(
          'get_collection_artists',
          { collection_id_param: collectionId }
        );
        
        if (rpcError) throw rpcError;
        
        console.log('RPC result type:', typeof rpcResult);
        console.log('RPC result:', rpcResult);
        
        if (rpcResult && typeof rpcResult === 'object') {
          console.log('RPC result keys:', Object.keys(rpcResult));
        }
        
        let artistList = [];
        
        // Handle the structure we're actually receiving
        if (rpcResult && rpcResult.artists && Array.isArray(rpcResult.artists)) {
          // We now know the artists are directly in the 'artists' field
          artistList = rpcResult.artists;
        } else if (rpcResult && Array.isArray(rpcResult)) {
          artistList = rpcResult;
        } else if (rpcResult && typeof rpcResult === 'object') {
          // Fallback to previous logic - try to get artists from object values
          artistList = Object.values(rpcResult).filter(value => 
            typeof value === 'object' && value !== null
          );
        }
        
        if (!artistList || artistList.length === 0) {
          setArtists([]);
          setLoading(false);
          return;
        }
        
        // Map the artist data to the format we need
        const processedArtists = artistList
          .filter(artist => artist !== null) // Filter out null artists
          .map(artist => ({
            uuid: artist.uuid || '',
            name: artist.artist_name || '',
            handle: artist.handle || (artist.uuid ? artist.uuid.toString() : ''),
            email: artist.email || '',
            instagram: artist.instagram || '',
            profile_img: artist.profile_img || '',
            artwork_count: artist.artwork_count || 0,
            bio: artist.bio || '',
            role: 'Artist' // Default role
          }));
        
        setArtists(processedArtists);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };
    
    if (collectionId && !isNaN(collectionId)) {
      fetchData();
    } else {
      console.warn('Invalid collectionId, not fetching data:', collectionId);
      setError('Invalid collection ID');
      setLoading(false);
    }
  }, [collectionId]);

  // Function to apply different card layouts
  // Function to apply different card layouts
const applyCardLayout = (doc, x, y, cardWidth, cardHeight, artist, qrCode, layout, showCutLines) => {
    // Draw card border (cut lines) only if showCutLines is enabled
    if (showCutLines) {
      doc.setDrawColor(0);
      doc.setLineWidth(0.01);
      doc.rect(x, y, cardWidth, cardHeight);
    }
  
    // Apply the selected layout
    switch(layout) {
      case 'minimal':
        // Minimal layout - clean, lots of whitespace
        doc.setFont(undefined, 'bold');
        doc.setFontSize(artistNameFontSize);
        doc.text(artist.name || 'Artist Name', x + cardWidth/2, y + 0.4, { align: 'center' });
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(roleFontSize);
        doc.text('Artist', x + cardWidth/2, y + 0.6, { align: 'center' });
        
        // Contact info centered
        doc.setFontSize(contactFontSize);
        if (artist.email) {
          doc.text(artist.email, x + cardWidth/2, y + 0.8, { align: 'center' });
        }
        if (artist.instagram) {
          doc.text(artist.instagram, x + cardWidth/2, y + 0.9, { align: 'center' });
        }
        
        // QR code centered at bottom
        if (qrCode && qrCode.qrDataUrl) {
          doc.addImage(
            qrCode.qrDataUrl,
            'PNG',
            x + (cardWidth - 0.7)/2,
            y + cardHeight - 0.9,
            0.7,
            0.7
          );
        }
        
        if (includeVibrantCastLogo && logoDataUrl) {
          doc.addImage(
            logoDataUrl,
            'PNG',
            x + (cardWidth - 1.0)/2,
            y + cardHeight - 0.5,
            1.0,
            0.3
          );
        }
        break;
        
      case 'creative':
        // Creative layout - diagonal elements, more visual interest
        // Add diagonal background element
        doc.setFillColor(240, 240, 255);
        doc.triangle(
          x, y,
          x + cardWidth, y,
          x, y + cardHeight
        );
        
        // Add name at an angle
        doc.setFont(undefined, 'bold');
        doc.setFontSize(artistNameFontSize);
        doc.text(artist.name || 'Artist Name', x + 0.2, y + 0.4);
        
        // Role text
        doc.setFont(undefined, 'normal');
        doc.setFontSize(roleFontSize);
        doc.text('Artist', x + 0.2, y + 0.6);
        
        // Contact info
        doc.setFontSize(contactFontSize);
        if (artist.email) {
          doc.text(artist.email, x + 0.2, y + 0.8);
        }
        if (artist.instagram) {
          doc.text(artist.instagram, x + 0.2, y + 0.9);
        }
        
        // QR code in bottom right with border
        if (qrCode && qrCode.qrDataUrl) {
          // Draw border around QR
          doc.setDrawColor(100, 100, 255);
          doc.setLineWidth(0.03);
          doc.rect(
            x + cardWidth - 0.95,
            y + cardHeight - 0.95,
            0.8,
            0.8
          );
          
          doc.addImage(
            qrCode.qrDataUrl,
            'PNG',
            x + cardWidth - 0.9,
            y + cardHeight - 0.9,
            0.7,
            0.7
          );
        }
        
        if (includeVibrantCastLogo && logoDataUrl) {
          doc.addImage(
            logoDataUrl,
            'PNG',
            x + 0.2,
            y + cardHeight - 0.5,
            1.0,
            0.3
          );
        }
        break;
        
      default: // standard layout
        // Standard layout (original implementation)
        doc.setFont(undefined, 'bold');
        doc.setFontSize(artistNameFontSize);
        let name = artist.name || 'Artist Name';
        
        // Handle long names
        if (name.length > 25) {
          const words = name.split(' ');
          let line1 = '';
          let line2 = '';
          let currentLine = line1;
          
          for (const word of words) {
            if ((currentLine + word).length < 25) {
              currentLine += (currentLine ? ' ' : '') + word;
            } else if (currentLine === line1) {
              // Move to second line
              line2 = word;
              currentLine = line2;
            } else {
              // Name is too long, truncate
              line2 += '...';
              break;
            }
          }
          
          doc.text(line1, x + 0.2, y + 0.4);
          if (line2) {
            doc.text(line2, x + 0.2, y + 0.6);
          }
        } else {
          doc.text(name, x + 0.2, y + 0.4);
        }
        
        // Add role text
        doc.setFont(undefined, 'normal');
        doc.setFontSize(roleFontSize);
        doc.text('Artist', x + 0.2, y + 0.6);
        
        // Add contact info (email and social)
        doc.setFontSize(contactFontSize);
        if (artist.email) {
          doc.text(artist.email, x + 0.2, y + 0.8);
        }
        if (artist.instagram) {
          doc.text(artist.instagram, x + 0.2, y + 0.9);
        }
        
        // Add QR code
        if (qrCode && qrCode.qrDataUrl) {
          doc.addImage(
            qrCode.qrDataUrl,
            'PNG',
            x + cardWidth - 0.9,
            y + cardHeight - 0.9,
            0.7,
            0.7
          );
          
          // Add URL below QR code
          if (qrCode.url) {
            doc.setFont(undefined, 'normal');
            doc.setFontSize(6);
            // Update to show the correct artwork page URL
            const shortUrl = "vibrantcast.com/artist/artworks";
            doc.text(shortUrl, x + cardWidth - 0.9 + 0.35, y + cardHeight - 0.1, { align: 'center' });
          }
        }
        
        // Add VibrantCast logo
        if (includeVibrantCastLogo && logoDataUrl) {
          doc.addImage(
            logoDataUrl,
            'PNG',
            x + 0.2,
            y + cardHeight - 0.5,
            1.0,
            0.3
          );
        }
        break;
    }
    
    return doc;
  };
  // Generate PDF when requested
  const generatePdf = async () => {
    try {
      console.log('Starting PDF generation...');
      console.log('Artists count:', artists.length);
      console.log('Logo loaded:', logoLoaded);
      console.log('Logo data URL length:', logoDataUrl ? logoDataUrl.length : 0);
      
      setGenerating(true);
      
      // Create PDF document (8.5 x 11 inches, portrait)
      console.log('jsPDF constructor:', typeof jsPDF);
      console.log('jsPDF version:', jsPDF.version);
      
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter'
      });
      
      console.log('PDF document created successfully');
      console.log('PDF document object:', doc);
      
      // Get template dimensions
      const template = AVERY_TEMPLATES[selectedTemplate];
      const cardWidth = template.cardWidth;
      const cardHeight = template.cardHeight;
      const margin = template.margin;
      const gap = template.gap;
      const cardsPerRow = template.cardsPerRow;
      const cardsPerCol = template.cardsPerCol;
      const cardsPerPage = cardsPerRow * cardsPerCol;
      
      // Add QR codes for each artist
      console.log('Generating QR codes for', artists.length, 'artists...');
      const qrPromises = artists
      .filter(artist => artist && artist.uuid) // Filter out artists without UUID
      .map(async (artist) => {
        try {
          // Use handle if available, fallback to uuid safely
          const artistHandle = artist.handle || (artist.uuid ? artist.uuid.toString() : '');
          // Link to the artist's artwork page, not just the artist page
          const url = `https://vibrantcast.com/artist/artworks?handle=${artistHandle}`;
          
          console.log(`Generating QR code for artist ${artist.name} (${artist.uuid})`);
          console.log('QRCode library:', typeof QRCode);
          console.log('QRCode.toDataURL method:', typeof QRCode.toDataURL);
          
          // Generate QR code with this URL
          const qrDataUrl = await QRCode.toDataURL(url, { 
            width: 120, 
            margin: 0,
            color: {
              dark: qrCodeColor,
              light: qrCodeBgColor
            },
            errorCorrectionLevel: 'M'
          });
          return { uuid: artist.uuid, qrDataUrl, url };
        } catch (err) {
          console.error(`Failed to generate QR code for artist:`, err);
          return { uuid: artist.uuid, qrDataUrl: null, url: null };
        }
      });
      
      const qrCodes = await Promise.all(qrPromises);
      console.log('QR codes generated successfully:', qrCodes.length);
      
      // Process each artist, packing them efficiently onto pages
      console.log('Processing artists for PDF layout...');
      for (let i = 0; i < artists.length; i++) {
        const artist = artists[i];
        const pageIndex = Math.floor(i / cardsPerPage);
        const positionOnPage = i % cardsPerPage;
        const row = Math.floor(positionOnPage / cardsPerRow);
        const col = positionOnPage % cardsPerRow;
        
        // Add new page if needed
        if (positionOnPage === 0 && i > 0) {
          doc.addPage();
        }
        
        // Calculate card position
        const x = margin + col * (cardWidth + gap);
        const y = margin + row * (cardHeight + gap);
        
        // Get QR code for this artist
        const qrCode = qrCodes.find(qr => qr.uuid === artist.uuid);
        
        // Apply the selected layout to the card
        applyCardLayout(doc, x, y, cardWidth, cardHeight, artist, qrCode, cardLayout, showCutLines);
      }
      
      // Convert to data URL and set state
      console.log('Converting PDF to data URL...');
      const dataUrl = doc.output('datauristring');
      console.log('PDF data URL generated, length:', dataUrl.length);
      setPdfDataUrl(dataUrl);
      console.log('PDF generation completed successfully!');
      console.log('pdfDataUrl state set to:', dataUrl ? 'Data URL present' : 'No data URL');
      console.log('State update triggered, should re-render component');
      
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setError('Failed to generate PDF: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Clear PDF preview
  const clearPreview = () => {
    setPdfDataUrl(null);
    // Restore normal scrolling
    document.body.style.overflow = 'auto';
  };

  // Set body overflow when showing PDF
  useEffect(() => {
    if (pdfDataUrl) {
      // Prevent background scrolling when PDF is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [pdfDataUrl]);

  console.log('Component rendering - pdfDataUrl:', pdfDataUrl ? 'Present' : 'Not present');
  
  return (
    <div className="artist-card-generator-wrapper">
      <div className="artist-card-generator-container">
        <div className="artist-card-generator-header">
          <h1>Artist Business Card Generator</h1>
          <div className="header-actions">
            <button 
              onClick={() => navigate(-1)}
              className="back-link"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#4F46E5',
                fontSize: '16px',
                padding: '8px 0',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <ChevronLeft size={20} />
              Back
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading artist data...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
            <Link to={`/gallery/${collectionId}`} className="button primary-button">
              Return to Collection
            </Link>
          </div>
        ) : (
          <div className="artist-card-generator-content">
            <div className="collection-info-card">
              <h2>{collection?.title || 'Collection'}</h2>
              <p>{artists.length} artists found in this collection</p>
              
              <div className="control-options">
                <div className="font-size-controls">
                  <div className="font-size-control">
                    <label>Name Size:</label>
                    <div className="font-size-slider">
                      <input 
                        type="range" 
                        min="10" 
                        max="18" 
                        value={artistNameFontSize} 
                        onChange={(e) => setArtistNameFontSize(parseInt(e.target.value))}
                      />
                      <span>{artistNameFontSize}pt</span>
                    </div>
                  </div>
                  
                  <div className="font-size-control">
                    <label>Role Size:</label>
                    <div className="font-size-slider">
                      <input 
                        type="range" 
                        min="8" 
                        max="14" 
                        value={roleFontSize} 
                        onChange={(e) => setRoleFontSize(parseInt(e.target.value))}
                      />
                      <span>{roleFontSize}pt</span>
                    </div>
                  </div>
                  
                  <div className="font-size-control">
                    <label>Contact Info Size:</label>
                    <div className="font-size-slider">
                      <input 
                        type="range" 
                        min="6" 
                        max="10" 
                        value={contactFontSize} 
                        onChange={(e) => setContactFontSize(parseInt(e.target.value))}
                      />
                      <span>{contactFontSize}pt</span>
                    </div>
                  </div>
                </div>

                <div className="layout-options">
                  <h4>Card Design</h4>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="cardLayout"
                        value="standard"
                        checked={cardLayout === 'standard'}
                        onChange={() => setCardLayout('standard')}
                      />
                      Standard
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="cardLayout"
                        value="minimal"
                        checked={cardLayout === 'minimal'}
                        onChange={() => setCardLayout('minimal')}
                      />
                      Minimal (Centered)
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="cardLayout"
                        value="creative"
                        checked={cardLayout === 'creative'}
                        onChange={() => setCardLayout('creative')}
                      />
                      Creative (Diagonal)
                    </label>
                  </div>
                </div>

                <div className="card-size-options">
                  <h4>Print Template</h4>
                  <select 
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {Object.entries(AVERY_TEMPLATES).map(([key, template]) => (
                      <option key={key} value={key}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  <p style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '8px'
                  }}>
                    {AVERY_TEMPLATES[selectedTemplate].cardsPerRow} × {AVERY_TEMPLATES[selectedTemplate].cardsPerCol} 
                    ({AVERY_TEMPLATES[selectedTemplate].cardsPerRow * AVERY_TEMPLATES[selectedTemplate].cardsPerCol} per page)
                  </p>
                </div>

                <div className="qr-code-options">
                  <h4>QR Code Colors</h4>
                  <div className="color-pickers">
                    <div className="color-option">
                      <label>Code Color:</label>
                      <input
                        type="color"
                        value={qrCodeColor}
                        onChange={(e) => setQrCodeColor(e.target.value)}
                      />
                    </div>
                    <div className="color-option">
                      <label>Background:</label>
                      <input
                        type="color"
                        value={qrCodeBgColor}
                        onChange={(e) => setQrCodeBgColor(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="logo-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeVibrantCastLogo}
                      onChange={(e) => setIncludeVibrantCastLogo(e.target.checked)}
                    />
                    Include VibrantCast Logo
                  </label>
                </div>
                
                <div className="control-actions">
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showCutLines}
                        onChange={(e) => setShowCutLines(e.target.checked)}
                      />
                      Show Cut Lines (for manual cutting)
                    </label>
                  </div>
                  <button
                    className="button primary-button"
                    onClick={generatePdf}
                    disabled={generating || artists.length === 0 || !logoLoaded}
                  >
                    {generating ? 'Generating...' : 'Generate PDF Business Cards'}
                  </button>
                </div>
              </div>
            </div>
            
            {pdfDataUrl ? (
              <div className="pdf-preview-container fullwidth">
                <div className="pdf-preview-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '20px',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #dee2e6'
                }}>
                  <div>
                    <h2 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>Display Business Cards</h2>
                    <p style={{ margin: '0', color: '#6b7280', fontSize: '16px' }}>
                      {artists.length} business cards created
                    </p>
                  </div>
                  
                  <div className="preview-actions" style={{
                    display: 'flex',
                    gap: '12px',
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                  }}>
                    <a
                      href={pdfDataUrl}
                      download={`artist-cards-${collectionId}.pdf`}
                      className="button primary-button"
                      style={{ 
                        textDecoration: 'none',
                        minWidth: '160px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                    <button
                      className="button text-button"
                      onClick={clearPreview}
                      style={{
                        minWidth: '160px'
                      }}
                    >
                      Close Preview
                    </button>
                  </div>
                </div>
                  
                {/* PDF Viewer Content Wrapper */}
                <div className="pdf-preview-content-wrapper">
                {/* PDF Viewer */}
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  background: '#f5f5f5',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 250px)'
                }}>
                      {/* Zoom and Print Controls */}
                      <div style={{
                        display: 'flex',
                        gap: '15px',
                        padding: '15px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #ddd',
                        width: '100%'
                      }}>
                        {/* Zoom Controls */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={() => setScale(Math.max(0.5, scale - 0.2))}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              background: '#fff',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Zoom out"
                          >
                            <ZoomOut size={16} />
                          </button>
                          
                          <span style={{ minWidth: '60px', textAlign: 'center', fontWeight: 'bold' }}>
                            {Math.round(scale * 100)}%
                          </span>
                          
                          <button
                            onClick={() => setScale(Math.min(2.0, scale + 0.2))}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              background: '#fff',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Zoom in"
                          >
                            <ZoomIn size={16} />
                          </button>
                          
                          <button
                            onClick={() => setScale(1.0)}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              background: '#fff',
                              fontSize: '12px'
                            }}
                            title="Reset zoom"
                          >
                            Reset
                          </button>
                        </div>
                        
                        {/* Divider */}
                        <div style={{ width: '1px', height: '24px', background: '#ddd' }}></div>
                        
                        {/* Print Button */}
                        <button
                          onClick={() => {
                            // Convert data URL to blob and create object URL
                            try {
                              fetch(pdfDataUrl)
                                .then(res => res.blob())
                                .then(blob => {
                                  const blobUrl = URL.createObjectURL(blob);
                                  const printWindow = window.open(blobUrl, '_blank');
                                  if (printWindow) {
                                    printWindow.addEventListener('load', () => {
                                      setTimeout(() => {
                                        printWindow.print();
                                      }, 250);
                                    });
                                    // Clean up the blob URL after printing
                                    printWindow.addEventListener('afterprint', () => {
                                      URL.revokeObjectURL(blobUrl);
                                    });
                                  }
                                })
                                .catch(err => {
                                  console.error('Error printing PDF:', err);
                                  alert('Failed to print PDF. Please try downloading instead.');
                                });
                            } catch (err) {
                              console.error('Error in print handler:', err);
                              alert('Failed to print PDF. Please try downloading instead.');
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            border: 'none',
                            borderRadius: '4px',
                            background: '#4CAF50',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          title="Print PDF"
                        >
                          <Printer size={16} />
                          Print
                        </button>
                      </div>
                      
                      {/* PDF Page Container */}
                      <div style={{
                        background: '#fff',
                        padding: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        flex: 1,
                        overflowY: 'auto'
                      }}>
                        <div style={{
                          transform: `scale(${scale})`,
                          transformOrigin: 'top center',
                          transition: 'transform 0.2s ease-in-out'
                        }}>
                          <Document 
                            file={pdfDataUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<p style={{ padding: '20px', textAlign: 'center' }}>Loading PDF...</p>}
                            error={<p style={{ padding: '20px', textAlign: 'center', color: 'red' }}>Failed to load PDF</p>}
                          >
                            <Page pageNumber={currentPage} width={612} />
                          </Document>
                        </div>
                      </div>
                    
                    {/* Navigation Controls */}
                    {numPages && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '15px',
                        backgroundColor: '#fff',
                        padding: '15px',
                        borderTop: '1px solid #ddd',
                        width: '100%'
                      }}>
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage <= 1}
                          style={{
                            padding: '10px 16px',
                            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                            opacity: currentPage <= 1 ? 0.5 : 1,
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            fontSize: '14px'
                          }}
                        >
                          ← Previous Page
                        </button>
                        
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Page</span>
                          <input
                            type="number"
                            min="1"
                            max={numPages}
                            value={currentPage}
                            onChange={(e) => {
                              const page = Math.max(1, Math.min(numPages, parseInt(e.target.value) || 1));
                              setCurrentPage(page);
                            }}
                            style={{
                              width: '60px',
                              padding: '6px 8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              textAlign: 'center',
                              fontSize: '14px'
                            }}
                          />
                          <span style={{ fontSize: '14px' }}>of {numPages}</span>
                        </div>
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                          disabled={currentPage >= numPages}
                          style={{
                            padding: '10px 16px',
                            cursor: currentPage >= numPages ? 'not-allowed' : 'pointer',
                            opacity: currentPage >= numPages ? 0.5 : 1,
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            fontSize: '14px'
                          }}
                        >
                          Next Page →
                        </button>
                      </div>
                    )}
                </div>
                </div>
              </div>
            ) : (
              artists.length > 0 && (
                <div className="artist-list-container">
                  <h3>Artists in Collection</h3>
                  <p className="help-text">
  Business cards will be generated with each artist's name, role, contact information,
  and a QR code linking to their VibrantCast profile at <code>vibrantcast.com/artist?handle={artists.length > 0 ? (artists[0].handle || artists[0].uuid) : ''}</code>.
  Use the controls above to customize the appearance of the cards.
</p>
                  
                  <div className="artists-grid">
                    {artists.map(artist => (
                      <div key={artist.uuid} className="artist-card">
                        {artist.profile_img && (
                          <img 
                            src={artist.profile_img}
                            alt={artist.name}
                            className="artist-image"
                          />
                        )}
                        <div className="artist-details">
                          <div className="artist-name">{artist.name}</div>
                          <div className="artist-role">Artist</div>
                          <div className="artist-email">{artist.email || 'No email provided'}</div>
                          <div className="artist-social">{artist.instagram || 'No Instagram provided'}</div>
                          <div className="artist-artwork-count">{artist.artwork_count} artworks in collection</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
            
            {artists.length === 0 && !loading && !error && (
              <div className="empty-state">
                <h3>No Artists Found</h3>
                <p>This collection doesn't have any artists yet.</p>
                <Link to={`/gallery/${collectionId}`} className="button secondary-button">
                  Return to Collection
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistCardGenerator; 